import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Target, Phone, Mail, MessageSquare, FileText, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const plays = [
  {
    title: 'The Stalled Deal Recovery Play',
    when: 'A deal has been in the same stage for 14+ days with no activity.',
    steps: [
      'Forsyt flags the deal as "Stalled" in Risk Radar with a red health score.',
      'Check the contact map, is the deal single-threaded? If so, Forsyt recommends multi-threading.',
      'Send a "pattern interrupt" email, acknowledge the stall and offer a new angle (case study, ROI data, exec intro).',
      'If no response in 48 hours, escalate: have your VP reach out to their VP with a mutual value prop.',
      'Log the outcome. Forsyt updates the deal stage and health score automatically.',
    ],
  },
  {
    title: 'The First-Week Rep Onboarding Play',
    when: 'A new rep joins the team and needs to ramp fast.',
    steps: [
      'Set the rep up in Forsyt with the "Sales Rep" role. They immediately see their assigned accounts.',
      'Day 1: Rep reviews their "Today\'s Focus" card, Forsyt has already prioritized their book of business.',
      'Day 2-3: Rep uses AI Deal Chat to get context on each deal ("What happened in the last call with Acme?").',
      'Day 4-5: Rep completes first round of outreach. Forsyt auto-logs activity and updates CRM fields.',
      'End of Week 1: Manager reviews rep\'s pipeline in Team View and coaches on top 3 priority deals.',
    ],
  },
  {
    title: 'The Quarterly Pipeline Cleanup Play',
    when: 'Quarter starts and you need a clean, honest pipeline.',
    steps: [
      'Open Risk Radar and filter by "Critical" and "At Risk" deals.',
      'For each Critical deal: Has there been any activity in 30 days? If no, move to Lost or push to next quarter.',
      'For each At Risk deal: Schedule a "deal review" 1:1 with the rep. Use AI Deal Chat to prep context.',
      'Set close date accuracy expectations, Forsyt tracks how often close dates are pushed.',
      'End result: A pipeline you can actually forecast from, cleaned in one afternoon instead of one week.',
    ],
  },
  {
    title: 'The Multi-Threading Play',
    when: 'A deal is single-threaded to one contact and the risk is high.',
    steps: [
      'Forsyt detects single-threaded deals automatically and flags them in Risk Radar.',
      'Identify the power map: Who else in the org has influence on this decision?',
      'Draft a warm intro request to your champion: "Who else should be involved in evaluating this?"',
      'If the champion is unresponsive, go around: LinkedIn, events, mutual connections.',
      'Target 3+ contacts per deal for Stage 3+. Forsyt tracks contact count per opportunity.',
    ],
  },
  {
    title: 'The End-of-Quarter Push Play',
    when: 'Last 2 weeks of the quarter. Time to close everything that can close.',
    steps: [
      'Open Opportunities and filter by Stage: Negotiation/Proposal + Close Date: This Quarter.',
      'For each deal, check the Forsyt risk score. Focus only on deals scored "Healthy" or "Needs Attention".',
      'Use AI Deal Chat: "What\'s the main blocker on the Acme deal?" to get instant context.',
      'Create urgency: Limited-time incentive, pricing hold, or exec sponsorship offer.',
      'Daily standup with Forsyt\'s Team View: Track which deals moved, which stalled, and adjust plays.',
    ],
  },
];

export default function Playbook() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Revenue Playbook, Forsyt"
        description="Battle-tested sales plays for every stage of the revenue cycle. Stalled deal recovery, pipeline cleanup, multi-threading, and more, powered by Forsyt."
        path="/resources/playbook"
      />

      {/* Hero */}
      <section className="pt-20 pb-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FileText className="w-10 h-10 text-blue-600 mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="playbook-headline">
            The Forsyt Revenue Playbook
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Battle-tested plays for every stage of the revenue cycle. Each one is designed to work with Forsyt's action prescriptions and pipeline intelligence.
          </p>
        </div>
      </section>

      {/* Plays */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          {plays.map((play, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all" data-testid={`play-${i}`}>
              <div className="flex items-start gap-3 mb-2">
                <Target className="w-6 h-6 text-blue-600 shrink-0 mt-1" />
                <h2 className="text-xl font-bold text-gray-900">{play.title}</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6 ml-9">When to run: {play.when}</p>
              <div className="space-y-3 ml-9">
                {play.steps.map((step, j) => (
                  <div key={j} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-gray-700 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">See these plays in action</h2>
          <p className="text-gray-600 mb-8">Try the sandbox to see how Forsyt surfaces the right play at the right time, automatically.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-8 py-3.5 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-all"
          >
            Try the Sandbox <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
